import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, ArrowUpRight, Factory, FileText, Globe2, LockKeyhole, Play, Settings, ShieldCheck, Users } from "lucide-react";
import axios from "axios";
import { API } from "@/context/AuthContext";

const FACTORY = "https://images.pexels.com/photos/34783124/pexels-photo-34783124.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABSTRACT = "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80";


export default function Landing() {
  const nav = useNavigate();
  const [niches, setNiches] = useState([]);

  useEffect(() => {
    axios.get(`${API}/network/stats`).catch(() => {});
    axios.get(`${API}/niches`).then(res => setNiches(res.data)).catch(err => console.error("Failed to load niches", err));
  }, []);

  const startBuyerFunnel = (niche) => {
    if (niche) {
      sessionStorage.setItem("askwinn_funnel_niche", niche.key);
      sessionStorage.setItem("askwinn_funnel_niche_label", niche.label);
      sessionStorage.setItem("askwinn_pending_sector", niche.label);
      nav(`/start/chat?niche=${niche.key}`);
    } else {
      nav("/start");
    }
  };

  return (
    <div className="min-h-screen bg-bone text-ink">
      <Navbar />

      {/* HERO */}
      <section className="landing-hero relative overflow-hidden">
        <div className="landing-hero-inner max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-14 grid lg:grid-cols-[0.92fr_1.08fr] gap-12 items-center">
          <div className="flex flex-col justify-center reveal">
            <div className="landing-eyebrow">PRIVATE LABEL MANUFACTURING, SIMPLIFIED</div>
            <h1 className="font-serif text-[3.45rem] sm:text-[4.7rem] lg:text-[5.65rem] font-light leading-[0.9] tracking-[-0.045em]">
              Don’t search for <em className="text-klein not-italic">manufacturers</em>.<br />
              Make them <span className="landing-bid-word">bid</span> for your brand.
            </h1>
            <p className="mt-7 max-w-xl text-base lg:text-[1.05rem] leading-relaxed text-[--muted-foreground]">
              AskWinn is the closed bidding engine where verified Indian manufacturers compete for your order. You set the brief — they bring the bids.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => startBuyerFunnel(null)} className="landing-cta landing-cta-orange" data-testid="hero-buyer-btn">
                Post an RFQ <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/how-it-works" className="landing-cta landing-cta-light"><Play className="w-4 h-4 fill-current" /> See how AskWinn works</Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-4 landing-proof">
              <span>TRUSTED</span><i /> <span>PRIVATE</span><i /> <span>EFFICIENT</span><i /> <span>BUILT FOR BRANDS</span>
            </div>
          </div>
          <div className="landing-hero-visual relative reveal-2 min-h-[390px] lg:min-h-[445px]">
            <div className="landing-dashboard">
              <div className="landing-dashboard-top"><span className="text-burn">●</span><span className="font-serif text-lg">Ask<span className="text-klein">Winn</span>.</span><span className="ml-auto text-xs">♧ &nbsp; <b>BB</b> Brand Builder⌄</span></div>
              <div className="landing-dashboard-body">
                <aside><span>♙ &nbsp; Dashboard</span><strong>▣ &nbsp; My RFQs</strong><span>♡ &nbsp; Messages</span><span>♢ &nbsp; Shortlisted</span><span>▧ &nbsp; Orders</span></aside>
                <main><div className="flex items-center justify-between"><b>Your Requirement</b><span className="landing-live">● Bidding Live</span></div><div className="landing-requirement"><div className="landing-product-image">🧴</div><div><b>Vitamin C Face Serum</b><p>30ml | Private Label | 10,000 units</p><small>Brightening face serum with natural ingredients.<br />Looking for custom formulation and packaging.</small><div className="flex gap-1 mt-2"><em>Skincare</em><em>Beauty & Cosmetics</em><em>Private Label</em></div></div></div><div className="landing-bids"><div className="flex justify-between mb-2"><b>Bids from Verified Manufacturers</b><small>View all →</small></div>{[["Manufacturer A","₹ 62 /unit","MOQ: 10,000 units"],["Manufacturer B","₹ 68 /unit","MOQ: 5,000 units"],["Manufacturer C","₹ 71 /unit","MOQ: 10,000 units"]].map(([name, price, moq]) => <div className="landing-bid" key={name}><span className="text-klein text-xl">♙</span><div><b>{name}</b><strong>{price}</strong><small>{moq} &nbsp;|&nbsp; Lead time: 30 days</small></div><button>View Bid</button></div>)}</div></main>
              </div>
            </div>
            <a className="landing-video-card" href="https://www.youtube.com/shorts/helx8ec6xdA" target="_blank" rel="noopener noreferrer" aria-label="Watch how AskWinn works on YouTube" data-testid="landing-video-link"><div className="landing-video-image"><img src="https://i.ytimg.com/vi/helx8ec6xdA/hqdefault.jpg" width="480" height="360" loading="lazy" decoding="async" alt="" /><span><Play className="w-5 h-5 fill-current" /></span></div><div className="font-serif text-2xl leading-none">See how<br />AskWinn works</div><small>WATCH 45 SEC</small></a>
            <div className="landing-label-card"><div>YOUR<br />BRAND<br />HERE</div><div className="landing-bottle">◯</div><small>BEAUTY<br />LIVES FURTHER</small></div>
          </div>
        </div>
      </section>

      <section className="landing-benefits border-y border-[--border-soft]"><div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-5">{[[ShieldCheck, "Verified manufacturers"], [Users, "Closed & confidential bidding"], [FileText, "Structured requirements"], [Settings, "Multiple industries & categories"], [Globe2, "Pan-India network"]].map(([Icon, label]) => <div key={label} className="landing-benefit"><Icon /><span>{label}</span></div>)}</div></section>
      <section className="landing-categories max-w-[1400px] mx-auto px-6 lg:px-10">
        <h2 className="font-serif text-3xl lg:text-4xl leading-none">What are you<br />building?</h2>
        {niches.map((niche) => (
          <button 
            key={niche.key} 
            onClick={() => startBuyerFunnel(niche)} 
            className="landing-category"
          >
            <span className="text-2xl">{niche.icon || "⚙️"}</span>
            <span className="font-medium">{niche.label}</span>
            {niche.opportunity_teaser && (
              <span className="text-[10px] leading-tight opacity-80 line-clamp-2">
                {niche.opportunity_teaser}
              </span>
            )}
            {niche.sub_categories && niche.sub_categories.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1 mt-1">
                {niche.sub_categories.slice(0, 3).map((sub, i) => (
                  <span key={i} className="text-[8px] bg-white/50 px-1 rounded">{sub}</span>
                ))}
              </div>
            )}
          </button>
        ))}
        <button onClick={() => startBuyerFunnel(null)} className="landing-category text-[--muted-foreground]">
          <span className="text-2xl">＋</span>
          <span>More categories<br />coming soon</span>
        </button>
      </section>


      {/* TRUST */}
      <section className="landing-trust max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="landing-trust-intro">
          <div>
            <div className="landing-eyebrow">TRUSTED MANUFACTURING NETWORK</div>
            <h2 className="font-serif text-5xl lg:text-7xl font-light leading-[0.9] tracking-[-0.04em]">
              Every manufacturer,<br /><em className="text-klein not-italic">verified</em>.
            </h2>
            <div className="landing-trust-rule" />
            <p className="text-lg text-[--muted-foreground] leading-relaxed">
              Our ops team checks business licences, factory audits, and capacity against actual orders. You won't find drop-shippers wearing factory uniforms here.
            </p>
          </div>
          <div className="landing-trust-visual">
            <img src="/verified-manufacturer-network.png" alt="Verified manufacturer network with factory and product packaging" />
          </div>
        </div>
        <div className="landing-trust-cards">
          {[
            [FileText, "Business licence & tax ID verified", "We verify business registration and tax credentials."],
            [Factory, "Factory walkthrough video on record", "Our team reviews and records factory walkthroughs."],
            [Users, "Minimum 3 production references", "We check past production references before onboarding."],
            [LockKeyhole, "NDA + IP protection by default", "Your product ideas, requirements and IP stay protected."],
          ].map(([Icon, title, description]) => (
            <div className="landing-trust-card" key={title}><span className="landing-trust-icon"><Icon /></span><div><h3>{title}</h3><p>{description}</p></div></div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="landing-final-cta max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-12">
        <div className="bg-klein text-white p-8 lg:p-12 relative overflow-hidden">
          <img src={ABSTRACT} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-30 mix-blend-screen" />
          <div className="relative max-w-3xl">
            <div className="overline text-white/70 mb-4">READY?</div>
            <h2 className="font-serif text-4xl lg:text-6xl font-light leading-[0.95] tracking-tight mb-7">
              Your next SKU is<br />one RFQ away.
            </h2>
            <button onClick={() => startBuyerFunnel(null)} className="btn-accent" data-testid="cta-start-btn">
              Start as a buyer <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="font-serif text-4xl lg:text-5xl tracking-tight leading-none">{n}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[--muted-foreground] mt-2">{l}</div>
  </div>
);
